import type { GenerationSettings } from '../../pages/GenerateTimetable/MockData';
import { generationSteps } from '../../pages/GenerateTimetable/MockData';
import type { Timetable } from '../../types';
import { generateMockLessons } from '../mockData/lessons';

/**
 * Mock timetable generation algorithm
 * Simulates the generation process with realistic delays and progress updates
 */
export class MockTimetableGenerator {
  private settings: GenerationSettings;
  private onProgress?: (progress: number, step: string, message: string) => void;
  private shouldStop = false;

  constructor(settings: GenerationSettings) {
    this.settings = settings;
  }

  /**
   * Set progress callback
   */
  public setProgressCallback(callback: (progress: number, step: string, message: string) => void): void {
    this.onProgress = callback;
  }

  /**
   * Stop the generation process
   */
  public stop(): void {
    this.shouldStop = true;
  }

  /**
   * Main generation method
   * Returns a Promise that resolves with the generated timetable
   */
  public async generate(): Promise<Timetable> {
    this.shouldStop = false;
    const startTime = new Date();

    // Simulate generation steps with delays
    for (const step of generationSteps) {
      if (this.shouldStop) {
        throw new Error('Generation stopped by user');
      }

      // Report progress
      if (this.onProgress) {
        this.onProgress(step.progress, step.step, step.message);
      }

      // Simulate processing time (faster for quick profile, slower for strict)
      const baseDelay = this.settings.profileId === 'profile-3' ? 50 : this.settings.profileId === 'profile-1' ? 200 : 120;
      const delay = baseDelay + Math.random() * 100;
      await this.delay(delay);
    }

    // Generate the actual timetable
    const timetableId = `timetable-${Date.now()}`;
    const lessons = generateMockLessons(timetableId);

    // Calculate stats based on settings
    const conflicts = this.calculateConflicts();
    const softRuleViolations = this.calculateSoftRuleViolations();

    const endTime = new Date();

    const timetable: Timetable = {
      id: timetableId,
      name: `Generated Timetable - ${new Date().toLocaleString()}`,
      academicYear: '2025-2026',
      createdAt: startTime.toISOString(),
      createdBy: 'admin-1',
      lastModified: endTime.toISOString(),
      status: 'generated',
      lessons,
      stats: {
        totalLessons: lessons.length,
        conflictCount: conflicts,
        ruleViolations: {
          hard: 0, // Mock generator never violates hard rules
          soft: softRuleViolations,
        },
        curriculumCoverage: this.calculateCoverage(),
        teacherLoad: [],
        roomUtilization: [],
        teacherPreferenceSatisfaction: this.calculateTeacherSatisfaction(),
        distributionScore: this.calculateDistributionScore(),
      },
      version: 1,
      isFinalized: false,
      finalizedYearGroups: [],
    };

    return timetable;
  }

  /**
   * Helper: delay for simulation
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Calculate conflicts based on settings
   */
  private calculateConflicts(): number {
    // Fewer conflicts with strict profile, more with quick
    const base = this.settings.profileId === 'profile-1' ? 5 : this.settings.profileId === 'profile-3' ? 20 : 10;
    return Math.max(0, base - Math.floor(Math.random() * 5));
  }

  /**
   * Calculate soft rule violations
   */
  private calculateSoftRuleViolations(): number {
    if (!this.settings.allowSoftRuleViolations) return 0;
    const max = this.settings.maxSoftRuleViolations;
    return Math.floor(Math.random() * max);
  }

  /**
   * Calculate curriculum coverage
   */
  private calculateCoverage(): number {
    // Higher coverage with strict profile
    const base = this.settings.profileId === 'profile-1' ? 98 : this.settings.profileId === 'profile-3' ? 92 : 95;
    return Math.min(100, base + Math.random() * 3);
  }

  /**
   * Calculate teacher preference satisfaction
   */
  private calculateTeacherSatisfaction(): number {
    // Higher with teacher-focused profile
    const base = this.settings.prioritizeTeacherWellbeing ? 90 : 80;
    return Math.min(100, base + Math.random() * 8);
  }

  /**
   * Calculate distribution score
   */
  private calculateDistributionScore(): number {
    // Higher with balanced profile
    const base = this.settings.profileId === 'profile-2' ? 92 : 85;
    return Math.min(100, base + Math.random() * 6);
  }
}

/**
 * Convenience function to generate a timetable
 */
export async function generateTimetable(
  settings: GenerationSettings,
  onProgress?: (progress: number, step: string, message: string) => void,
): Promise<Timetable> {
  const generator = new MockTimetableGenerator(settings);
  if (onProgress) {
    generator.setProgressCallback(onProgress);
  }
  return generator.generate();
}
